import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ALTCHA_CHALLENGE_URL } from "../config/env";

const LazyAltcha = forwardRef(function LazyAltcha(
  { onVerify, visible = false },
  ref
) {
  const [status, setStatus] = useState("idle"); // idle, fetching, solving, verified, error
  const [token, setToken] = useState("");
  const [showVisible] = useState(visible);
  const [errorMessage, setErrorMessage] = useState("");

  const tokenRef = useRef("");
  const solvingPromiseRef = useRef(null);
  const challengeDataRef = useRef(null);
  const solvedAtRef = useRef(0);

  // Sync token ref
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const startSolving = async () => {
    if (solvingPromiseRef.current) {
      return solvingPromiseRef.current;
    }

    // If token is still fresh (< 4 minutes), reuse
    if (tokenRef.current && Date.now() - solvedAtRef.current < 240000) {
      return tokenRef.current;
    }

    solvingPromiseRef.current = (async () => {
      try {
        setStatus("fetching");
        setErrorMessage("");

        const resp = await fetch(ALTCHA_CHALLENGE_URL, { credentials: "same-origin" });
        if (!resp.ok) {
          throw new Error("Gagal mengambil tantangan ALTCHA");
        }

        const data = await resp.json();
        challengeDataRef.current = data;

        setStatus("solving");

        // Solve PoW using SubtleCrypto / Web Worker
        const number = await solvePoW(data.challenge, data.salt, data.maxnumber || 50000);

        if (number === null) {
          throw new Error("Solusi PoW tidak ditemukan");
        }

        const payloadObj = {
          algorithm: data.algorithm || "SHA-256",
          challenge: data.challenge,
          number: number,
          salt: data.salt,
          signature: data.signature,
        };

        const b64Token = btoa(JSON.stringify(payloadObj));
        setToken(b64Token);
        tokenRef.current = b64Token;
        solvedAtRef.current = Date.now();
        setStatus("verified");

        if (onVerify) {
          onVerify(b64Token);
        }

        return b64Token;
      } catch (err) {
        console.warn("ALTCHA error:", err);
        setStatus("error");
        setErrorMessage(err.message || "Gagal verifikasi");
        return "";
      } finally {
        solvingPromiseRef.current = null;
      }
    })();

    return solvingPromiseRef.current;
  };

  const reset = () => {
    setToken("");
    tokenRef.current = "";
    solvedAtRef.current = 0;
    setStatus("idle");
    setErrorMessage("");
    startSolving();
  };

  const getToken = async () => {
    if (tokenRef.current && Date.now() - solvedAtRef.current < 240000) {
      return tokenRef.current;
    }
    return await startSolving();
  };

  useImperativeHandle(ref, () => ({
    reset,
    getToken,
    solve: startSolving,
  }));

  useEffect(() => {
    const handleInteraction = (event) => {
      if (!event || !event.isTrusted) {
        return;
      }
      startSolving();
      removeListeners();
    };

    const removeListeners = () => {
      document.removeEventListener("pointerover", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
      document.removeEventListener("focusin", handleInteraction);
    };

    const timer = setTimeout(() => {
      document.addEventListener("pointerover", handleInteraction, { once: true, passive: true });
      document.addEventListener("touchstart", handleInteraction, { once: true, passive: true });
      document.addEventListener("focusin", handleInteraction, { once: true, passive: true });
    }, 150);

    return () => {
      clearTimeout(timer);
      removeListeners();
    };
  }, []);

  if (!showVisible) {
    return (
      <div className="altcha-invisible" aria-hidden="true" style={{ display: "none" }} />
    );
  }

  return (
    <div className="altcha-container" style={{ margin: "12px 0", fontSize: "0.88rem" }}>
      <div
        className="altcha-box"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "10px",
          padding: "8px 14px",
          borderRadius: "8px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          color: "#334155",
        }}
      >
        {status === "verified" ? (
          <span style={{ color: "#16a34a", fontWeight: "600" }}>✓ Terverifikasi Aman</span>
        ) : status === "solving" || status === "fetching" ? (
          <span style={{ color: "#0284c7" }}>⏳ Memverifikasi keamanan...</span>
        ) : status === "error" ? (
          <span style={{ color: "#dc2626" }}>
            ❌ {errorMessage}{" "}
            <button
              type="button"
              onClick={reset}
              style={{
                background: "none",
                border: "none",
                color: "#2563eb",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Coba lagi
            </button>
          </span>
        ) : (
          <span>Secured by ALTCHA</span>
        )}
      </div>
    </div>
  );
});

async function solvePoW(challenge, salt, maxNumber) {
  if (typeof window === "undefined" || !window.crypto || !window.crypto.subtle) {
    return fallbackSolvePoW(challenge, salt, maxNumber);
  }

  const encoder = new TextEncoder();
  const target = challenge.toLowerCase();
  const chunkSize = 2500;

  for (let i = 0; i <= maxNumber; i += chunkSize) {
    const limit = Math.min(i + chunkSize, maxNumber);
    for (let current = i; current <= limit; current++) {
      const input = encoder.encode(salt + current);
      const hashBuf = await crypto.subtle.digest("SHA-256", input);
      const hashArray = new Uint8Array(hashBuf);
      let hex = "";
      for (let b = 0; b < hashArray.length; b++) {
        hex += hashArray[b].toString(16).padStart(2, "0");
      }
      if (hex === target) {
        return current;
      }
    }
    // Yield execution to keep UI smooth
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return null;
}

function fallbackSolvePoW() {
  // Simple fallback if subtle crypto is unavailable
  return 0;
}

export default LazyAltcha;
